import {inject, Injectable} from '@angular/core';
import {UserService} from "./user.service";
import {UserDto} from "../repositories/dto/user.dto";
import {SettingKey} from "../base/setting-key.enum";
import {LoggingService} from "./logger/logging.service";
import {BehaviorSubject, combineLatest, Observable} from "rxjs";
import {UserRepo} from "../repositories/api/user.repo";
import {SettingDto} from "../repositories/dto/setting.dto";
import {OrgService} from "./org.service";
import {OrgDto} from "../repositories/dto/org.dto";
import {filter} from "rxjs/operators";
import {isDefined, notDefined} from "../libs/utils/epic.utils";
import {RepoProvider} from "../repositories/repo.provider";

@Injectable({providedIn: 'root'})
export class SettingsService {

  public static readonly FIRST_STEPS_NAV = [
    SettingKey.ORG_FIRST_STEPS_ADDRESS,
    SettingKey.ORG_FIRST_STEPS_BANK_ADDRESS];

  public static readonly FIRST_STEPS = [
    SettingKey.ORG_FIRST_STEPS_ADDRESS,
    SettingKey.ORG_FIRST_STEPS_BANK_ADDRESS,
    SettingKey.ORG_FIRST_STEPS_CONTACT,
    SettingKey.ORG_FIRST_STEPS_INVOICE_DRAFT];

  private readonly userRepo: UserRepo = inject(RepoProvider).getRepo(UserRepo);
  private readonly userService: UserService = inject(UserService);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly orgService: OrgService = inject(OrgService);

  private readonly settingOnChange = new Map<string, BehaviorSubject<SettingDto | undefined>>();
  private readonly settingOldValue = new Map<string, string | undefined>();
  private readonly userSettings = new BehaviorSubject<SettingDto[]>([]);
  private readonly orgSettings = new BehaviorSubject<SettingDto[]>([]);

  private user: UserDto = new UserDto();
  private org: OrgDto | undefined;
  private persistUserTimeout: any;
  private persistOrgTimeout: any;
  private updateSettingsDelay = 2000;

  constructor() {
    this.userService.getUserDistinct().subscribe((user) => {
      this.user = user;
      this.loadUserSettings(user);
    });
    this.orgService.getCurrentOrg().subscribe((org) => {
      this.org = org;
      this.orgSettings.next(org && org.settings ? org.settings : []);
    });
  }

  public getUserSettings(): Observable<SettingDto[]> {
    return this.userSettings.asObservable().pipe(filter((s) => s.length > 0));
  }

  public getOrgSettings(): Observable<SettingDto[]> {
    return this.orgSettings.asObservable().pipe(filter((s) => s.length > 0));
  }

  public updateUserSetting(key: SettingKey, value: string): Promise<boolean> {
    return new Promise((resolve) => {
      const keyName = SettingKey[key];
      const setting = this.getUserSetting(key);
      if (isDefined(setting) && setting.value !== value) {
        this.log.trace('Settings: update existing setting with key "{}" and value "{}"', [keyName, value]);
        setting.value = value;
        this.writeUserSettings(this.userSettings.value).then((result) => resolve(result));
      } else if (notDefined(setting)) {
        this.log.trace('Settings: create new setting with key "{}" and value "{}"', [keyName, value]);
        const userSettings = this.userSettings.value;
        userSettings.push(SettingDto.of(keyName, value));
        this.writeUserSettings(userSettings).then((result) => resolve(result));
      } else {
        resolve(true);
      }
    });
  }

  public updateOrgSetting(key: SettingKey, value: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.org) {
        const keyName = SettingKey[key];
        const setting = this.getOrgSetting(key);
        if (isDefined(setting) && setting.value !== value) {
          this.log.trace('Settings: update existing setting with key "{}" and value "{}"', [keyName, value]);
          setting.value = value;
          this.writeOrgSettings(this.orgSettings.value).then((r) => resolve(r)).catch((r) => reject(r));
        } else if (notDefined(setting)) {
          this.log.trace('Settings: create new setting with key "{}" and value "{}"', [keyName, value]);
          const orgSettings = this.orgSettings.value;
          orgSettings.push(SettingDto.of(keyName, value));
          this.writeOrgSettings(orgSettings).then((r) => resolve(r)).catch((r) => reject(r));
        } else {
          resolve(true);
        }
      } else {
        this.log.error('Org ist not set');
        reject(false);
      }
    });
  }

  public getSettingsOnChange(keys: SettingKey[]): Observable<SettingDto[]> {
    return combineLatest(keys.map((k) => this.getSettingOnChange(k)));
  }

  public getSettingOnChange(key: SettingKey): Observable<SettingDto> {
    const keyName = SettingKey[key];
    if (this.settingOnChange.has(keyName)) {
      return this.settingOnChange.get(keyName)!.asObservable()
        .pipe(filter((s) => isDefined(s)));
    }

    const setting = key.startsWith('USER_') ? this.getUserSetting(key) : this.getOrgSetting(key);
    const event = new BehaviorSubject<SettingDto | undefined>(isDefined(setting) ? setting : undefined);
    this.settingOldValue.set(keyName, isDefined(setting) ? setting.value : undefined);
    this.settingOnChange.set(keyName, event);
    return event.asObservable().pipe(filter((s) => isDefined(s)));
  }

  private writeUserSettings(settings: SettingDto[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.setUserSettings(settings);
      clearTimeout(this.persistUserTimeout);
      this.persistUserTimeout = setTimeout(() => {
        this.userRepo.updateSettings(this.user, settings).subscribe({
          next: (w) => {
            this.setUserSettings(w.getDatas());
            resolve(true);
          },
          error: () => reject(false)
        });
      }, this.updateSettingsDelay);
    });
  }

  private writeOrgSettings(settings: SettingDto[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.setOrgSettings(settings);
      clearTimeout(this.persistOrgTimeout);
      this.persistOrgTimeout = setTimeout(() => {
        if (isDefined(this.org)) {
          this.orgService.updateSettings(this.org, settings).subscribe({
            next: (s) => {
              this.setOrgSettings(s);
              resolve(true);
            },
            error: () => reject(false)
          });
        }
      }, this.updateSettingsDelay);
    });
  }

  private loadUserSettings(user: UserDto): void {
    this.log.trace('Settings: loading user settings for "{}"', [user.id]);
    this.userRepo.readSettings(user)
      .subscribe((w) => this.setUserSettings(w.getDatas()));
  }

  private getUserSetting(key: SettingKey): SettingDto | undefined {
    const keyName = SettingKey[key];
    return this.userSettings.value.find((s) => s.key === keyName);
  }

  private getOrgSetting(key: SettingKey): SettingDto | undefined {
    if (this.orgSettings.value) {
      const keyName = SettingKey[key];
      return this.orgSettings.value.find((s) => s.key === keyName);
    }
    return undefined;
  }

  private setUserSettings(settings: SettingDto[]): void {
    if (settings.length > 0) {
      this.log.trace('Settings: set user settings: {}', [settings]);
      this.userSettings.next(settings);
      this.onSetSettings(settings);
    }
  }

  private setOrgSettings(settings: SettingDto[]): void {
    if (settings.length > 0) {
      this.log.trace('Settings: set org settings: {}', [settings]);
      this.orgSettings.next(settings);
      this.onSetSettings(settings);
    }
  }

  private onSetSettings(settings: SettingDto[]): void {
    settings
      .filter((s) => this.settingOnChange.has(s.key) && this.settingOldValue.has(s.key))
      .filter((s) => this.settingOldValue.get(s.key) !== s.value)
      .forEach((s) => {
        this.settingOnChange.get(s.key)?.next(s);
        this.settingOldValue.set(s.key, s.value);
      });
  }
}
